import { useEffect, useState } from "react";

import { MDXRemote, MDXRemoteSerializeResult } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import Link from "next/link";

import { GetSurveyDataProps } from "@/types";
import { Button, Modal, Table } from "react-bootstrap";

export default function GetSurveyDataTable({ data }: { data: GetSurveyDataProps[] }) {
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<string>("");
  const [parsedMdx, setParsedMdx] = useState<MDXRemoteSerializeResult | null>(null);
  const [selectedStudy, setSelectedStudy] = useState<string>("");

  const headers = ["STUDY", "LINK TO DOWNLOAD SURVEY DATA", "DATA ACCESS INSTRUCTIONS"];

  // Function to replace image slugs with actual URLs
  const processInstructionContent = (content: string, files: Array<{ label: string; url: string }>) => {
    let updatedContent = content;
    files.forEach((file) => {
      const regex = new RegExp(`!\\[.*?\\]\\(${file.label}\\)`, "g");
      updatedContent = updatedContent.replace(regex, `![](${file.url})`);
    });
    return updatedContent;
  };

  const handleShowModal = (
    study_name: string,
    instruction: { content: string; files: Array<{ label: string; url: string }> },
  ) => {
    const processedContent = processInstructionContent(instruction.content, instruction.files || []);
    setModalContent(processedContent);
    setSelectedStudy(study_name);
    setShowModal(true);
  };

  useEffect(() => {
    const parseMdx = async () => {
      try {
        if (modalContent) {
          const mdxSource = await serialize(modalContent, {
            mdxOptions: {
              remarkPlugins: [],
              rehypePlugins: [],
            },
          });
          setParsedMdx(mdxSource);
        }
      } catch (error) {
        console.error("Failed to parse MDX:", error);
      }
    };

    parseMdx();
  }, [modalContent]);

  return (
    <>
      <Table striped responsive>
        <thead>
          <tr className="g2-table-header-blue">
            {headers.map((item: string, index: number) => (
              <th key={index}>{item}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((row, index) => (
              <tr key={index}>
                <td>{row.study.label}</td>
                <td>
                  {row.url_extra_fields ? (
                    <>
                      <div>
                        {row.url_extra_fields?.url_label && <>{row.url_extra_fields?.url_label}: </>}
                        <a
                          href={row.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="g2-link-blue fw-semibold"
                        >
                          {row.url_extra_fields?.url_text || row.study.label}
                        </a>
                      </div>
                    </>
                  ) : (
                    <Link href={row.download_url} className="g2-link-blue" target="_blank">
                      {row.caption}
                    </Link>
                  )}
                </td>
                <td>
                  <i
                    className="bi bi-box-arrow-up-right g2-link-blue"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleShowModal(row.study.label, row.instruction)}
                  ></i>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length} className="text-center fst-italic">
                No results found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" aria-labelledby="country-policy-modal">
        <Modal.Header closeButton>
          <Modal.Title>{selectedStudy}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{parsedMdx && <MDXRemote {...parsedMdx} />}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
